import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { Box, IconButton, Typography } from "@mui/material";
import type { KeyboardEvent, MouseEvent } from "react";
import type { TableColumn } from "react-data-table-component";

import { CallPlanInfoFragment } from "@/graphql/graphql";
import { formatAccountCalendarDate } from "@/utils/date-utils";

import { StatusChip } from "../Playlists/components/StatusChip";
import { getCallPlanReactivateCandidate } from "./CreateCallPlanModal/CreateCallPlanModal.utils";

const CALL_PLAN_ACTION_ICON_SLOT_PX = 40;

const callPlanActionIconSlotSx = {
  width: CALL_PLAN_ACTION_ICON_SLOT_PX,
  minWidth: CALL_PLAN_ACTION_ICON_SLOT_PX,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
} as const;

const callPlanTableActionsCellSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  width: "100%",
  minWidth: CALL_PLAN_ACTION_ICON_SLOT_PX * 2,
} as const;

const stopTableRowClickPropagation = (e: MouseEvent | KeyboardEvent) => {
  e.stopPropagation();
};

type CallPlanTableActionsCellProps = {
  activePlan?: CallPlanInfoFragment;
  reactivatePlan?: CallPlanInfoFragment;
  editPlan?: CallPlanInfoFragment | null;
  onDeactivateCallPlan: (plan: CallPlanInfoFragment) => void;
  onReactivateCallPlan: (plan: CallPlanInfoFragment) => void;
  onEditCallPlan?: (plan: CallPlanInfoFragment) => void;
};

export const CallPlanTableActionsCell = ({
  activePlan,
  reactivatePlan,
  editPlan,
  onDeactivateCallPlan,
  onReactivateCallPlan,
  onEditCallPlan,
}: CallPlanTableActionsCellProps) => {
  const showDeactivate = !!activePlan;
  const showReactivate = !!reactivatePlan && !showDeactivate;
  const showEdit = Boolean(editPlan && onEditCallPlan);

  if (!showDeactivate && !showReactivate && !showEdit) {
    return null;
  }

  return (
    <Box
      sx={callPlanTableActionsCellSx}
      onClick={stopTableRowClickPropagation}
      onKeyDown={stopTableRowClickPropagation}
    >
      <Box sx={callPlanActionIconSlotSx}>
        {showDeactivate && activePlan && (
          <IconButton
            size="small"
            aria-label="Deactivate call plan"
            onClick={() => onDeactivateCallPlan(activePlan)}
          >
            <PauseCircleOutlineIcon fontSize="small" />
          </IconButton>
        )}
        {showReactivate && reactivatePlan && (
          <IconButton
            size="small"
            aria-label="Reactivate call plan"
            onClick={() => onReactivateCallPlan(reactivatePlan)}
          >
            <PlayCircleOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={callPlanActionIconSlotSx}>
        {showEdit && editPlan && onEditCallPlan && (
          <IconButton
            size="small"
            aria-label="Edit call plan"
            onClick={() => onEditCallPlan(editPlan)}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export const deactivateCallPlanConfirmDialogContent = (
  <Typography variant="body2">
    Deactivate this call plan? The end date will be set to now.
  </Typography>
);

export const reactivateCallPlanConfirmDialogContent = (
  <Typography variant="body2">
    Reactivate this call plan? The end date will be extended through today in the patient&apos;s
    timezone.
  </Typography>
);

export const CALL_PLAN_TABLE_COLUMN_IDS = {
  ROUND: "round",
  WEEK: "week",
  DATE_RANGE: "dateRange",
  ACTIVITY_COUNT: "activityCount",
  STATUS: "status",
} as const;

export type CallPlanTableRow = {
  rowKey: string;
  /** Same program week; expanded segments are ordered by segment end date (same as date-range column semantics). */
  segmentPlans: CallPlanInfoFragment[];
  /** Latest segment by startDate within the week */
  latestPlan: CallPlanInfoFragment;
  sortEndDate: string;
};

export const formatCallPlanDateRange = (
  plan: CallPlanInfoFragment,
  accountTimeZone?: string | null
): string => {
  const start = formatAccountCalendarDate(plan.startDate?.toString(), accountTimeZone);
  const end = plan.endDate
    ? formatAccountCalendarDate(plan.endDate.toString(), accountTimeZone)
    : "Active";
  return start ? `${start} - ${end}` : "-";
};

export const formatGroupedCallPlanDateRange = (
  row: CallPlanTableRow,
  accountTimeZone?: string | null
): string => {
  const earliestStart = row.segmentPlans.reduce(
    (min, p) => (p.startDate < min ? p.startDate : min),
    row.segmentPlans[0].startDate
  );
  const start = formatAccountCalendarDate(earliestStart?.toString(), accountTimeZone);
  const endedEnds = row.segmentPlans.map((p) => p.endDate).filter(Boolean) as string[];
  const hasActiveSegment = endedEnds.length < row.segmentPlans.length;
  const endDisplay = hasActiveSegment
    ? "Active"
    : formatAccountCalendarDate(
        endedEnds.reduce((max, e) => (e > max ? e : max), endedEnds[0]),
        accountTimeZone
      );
  return start ? `${start} - ${endDisplay}` : "-";
};

/**
 * One table row per program week: multiple segments in the same week are collapsed until expanded.
 */
export const buildCallPlanTableRows = (callPlans: CallPlanInfoFragment[]): CallPlanTableRow[] => {
  const byKey = callPlans.reduce<Record<string, CallPlanInfoFragment[]>>((acc, plan) => {
    const key = `${plan.program.id}:${plan.programWeek}`;
    (acc[key] ??= []).push(plan);
    return acc;
  }, {});

  return Object.values(byKey).map((plansInWeek) => {
    const latestPlan = plansInWeek.reduce((best, p) =>
      p.startDate.localeCompare(best.startDate) > 0 ? p : best
    );
    const endedEnds = plansInWeek.map((p) => p.endDate).filter(Boolean) as string[];
    const hasActiveSegment = endedEnds.length < plansInWeek.length;
    const sortEndDate = hasActiveSegment
      ? "9999-12-31" // Placeholder for when end date is null, to ensure it's always largest value
      : endedEnds.reduce((max, e) => (e > max ? e : max), endedEnds[0]);

    return {
      rowKey:
        plansInWeek.length === 1
          ? `single-${latestPlan.id}`
          : `group-${latestPlan.program.id}-${latestPlan.programWeek}`,
      segmentPlans: [...plansInWeek],
      latestPlan,
      sortEndDate,
    };
  });
};

/** Order grouped segments like the Start & End Date column (segment end dates; Active last when descending). */
export const sortSegmentPlansForTable = (
  plans: CallPlanInfoFragment[],
  direction: "asc" | "desc"
): CallPlanInfoFragment[] =>
  [...plans].sort((planA, planB) =>
    direction === "asc"
      ? planA.startDate.localeCompare(planB.startDate)
      : planB.startDate.localeCompare(planA.startDate)
  );

/**
 * When two or more plans share the same program and week (e.g. after splitting), only the
 * latest segment (by startDate) should stay editable; all older segments are read-only.
 */
export const duplicateProgramWeekEarlierPlanIds = (callPlans: CallPlanInfoFragment[]): number[] => {
  const byProgramAndWeek = callPlans.reduce<Record<string, CallPlanInfoFragment[]>>(
    (acc, callPlan) => {
      const key = `${callPlan.program.id}:${callPlan.programWeek}`;
      (acc[key] ??= []).push(callPlan);
      return acc;
    },
    {}
  );

  // Duplicates are all that are not the latest plan in a week.
  return Object.values(byProgramAndWeek).flatMap((callPlansWithinSameWeek) => {
    if (callPlansWithinSameWeek.length < 2) {
      return [];
    }

    // Find the latest plan in the week.
    const latest = callPlansWithinSameWeek.reduce((currentLatest, callPlan) =>
      callPlan.startDate.localeCompare(currentLatest.startDate) > 0 ? callPlan : currentLatest
    );

    return callPlansWithinSameWeek
      .filter((callPlan) => callPlan.id !== latest.id)
      .map((callPlan) => callPlan.id);
  });
};

export const buildCallPlanTableColumnsBase = (
  accountTimeZone?: string | null
): TableColumn<CallPlanTableRow>[] => [
  {
    id: CALL_PLAN_TABLE_COLUMN_IDS.ROUND,
    name: "Round",
    selector: ({ latestPlan: { program } }) => program?.round ?? "-",
  },
  {
    id: CALL_PLAN_TABLE_COLUMN_IDS.WEEK,
    name: "Week",
    selector: ({ latestPlan }) => latestPlan.programWeek,
  },
  {
    id: CALL_PLAN_TABLE_COLUMN_IDS.DATE_RANGE,
    name: "Start & End Date",
    selector: (row) => formatGroupedCallPlanDateRange(row, accountTimeZone),
    sortable: true,
    sortFunction: (rowA, rowB) => rowA.sortEndDate.localeCompare(rowB.sortEndDate),
  },
  {
    id: CALL_PLAN_TABLE_COLUMN_IDS.ACTIVITY_COUNT,
    name: "Activity Count",
    selector: ({ latestPlan }) => latestPlan.activityCount,
  },
  {
    id: CALL_PLAN_TABLE_COLUMN_IDS.STATUS,
    name: "Status",
    cell: ({ latestPlan }) => <StatusChip isActive={latestPlan.isActive} />,
  },
];

/** Single source for whether edit or delete is allowed and why it is blocked (for tooltips). */
export interface CallPlanEditOrDeleteState {
  disabled: boolean;
  disabledTooltip?: string;
}

export const getCallPlanEditState = (
  row: CallPlanInfoFragment,
  duplicateWeekEarlierPlanIds: number[],
  hasNoActiveProgram: boolean
): CallPlanEditOrDeleteState => {
  if (hasNoActiveProgram) {
    return {
      disabled: true,
      disabledTooltip: "Cannot edit call plan from a finished program.",
    };
  }

  const nowMs = Date.now();
  const planEnded = row.endDate != null && new Date(row.endDate).getTime() < nowMs;
  if (planEnded || duplicateWeekEarlierPlanIds.includes(row.id)) {
    return {
      disabled: true,
      disabledTooltip: "Cannot edit call plan that has already finished.",
    };
  }

  return { disabled: false };
};

export const getCallPlanDeleteState = (
  callPlan: CallPlanInfoFragment,
  duplicateWeekEarlierPlanIds: number[]
): CallPlanEditOrDeleteState => {
  if (duplicateWeekEarlierPlanIds.includes(callPlan.id)) {
    return {
      disabled: true,
      disabledTooltip: "Cannot delete this call plan.",
    };
  }

  if (Date.now() >= new Date(callPlan.startDate).getTime()) {
    return {
      disabled: true,
      disabledTooltip: "Cannot delete a call plan that has already started",
    };
  }

  return { disabled: false };
};

/** Past plans (end instant before now) are read-only; current and future rows show edit. */
export const buildCallPlanColumns = (
  onEditCallPlan: (row: CallPlanInfoFragment) => void,
  onDeactivateCallPlan: (plan: CallPlanInfoFragment) => void,
  onReactivateCallPlan: (plan: CallPlanInfoFragment) => void,
  callPlans: CallPlanInfoFragment[],
  duplicateWeekEarlierPlanIds: number[] = [],
  hasNoActiveProgram = false,
  accountTimeZone?: string | null
): TableColumn<CallPlanTableRow>[] => {
  return [
    ...buildCallPlanTableColumnsBase(accountTimeZone),
    {
      id: "actions",
      name: "",
      width: "88px",
      right: true,
      ignoreRowClick: true,
      button: true,
      cell: (row) => {
        const editState = getCallPlanEditState(
          row.latestPlan,
          duplicateWeekEarlierPlanIds,
          hasNoActiveProgram
        );
        const activePlan = row.segmentPlans.find((plan) => plan.isActive);
        const reactivatePlan = getCallPlanReactivateCandidate(
          row.segmentPlans,
          callPlans,
          accountTimeZone
        );

        return (
          <CallPlanTableActionsCell
            activePlan={activePlan}
            reactivatePlan={reactivatePlan}
            editPlan={editState.disabled ? null : row.latestPlan}
            onDeactivateCallPlan={onDeactivateCallPlan}
            onReactivateCallPlan={onReactivateCallPlan}
            onEditCallPlan={onEditCallPlan}
          />
        );
      },
    },
  ];
};
